import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, MapPin, Sparkles, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
export const Booking = () => {
  const {
    t
  } = useLanguage();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    serviceType: '',
    date: '',
    time: '',
    name: '',
    phone: '',
    address: '',
    notes: ''
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTimeout(() => {
      setIsSubmitted(true);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 800);
  };
  const fadeInUp = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  if (isSubmitted) {
    return <div className="w-full min-h-screen bg-pink-lighter/30 pt-32 pb-20 flex items-center justify-center" data-id="element-146">
        <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="bg-white p-12 rounded-[2rem] shadow-xl max-w-lg w-full mx-4 text-center border border-pink-50" data-id="element-147">
          <div className="w-24 h-24 bg-pink-lighter rounded-full flex items-center justify-center text-pink-primary mx-auto mb-8" data-id="element-148">
            <Sparkles className="w-12 h-12" data-id="element-149" />
          </div>
          <h2 className="text-3xl font-bold text-navy mb-4" data-id="element-150">
            {t('booking.form.successTitle')}
          </h2>
          <p className="text-gray-600 text-lg mb-8 leading-relaxed" data-id="element-151">
            {t('booking.form.successMsg')}
          </p>
          <button onClick={() => {
          setIsSubmitted(false);
          setFormData({
            serviceType: '',
            date: '',
            time: '',
            name: '',
            phone: '',
            address: '',
            notes: ''
          });
        }} className="bg-pink-primary text-white px-8 py-3 rounded-full font-medium hover:bg-pink-hover transition-colors shadow-pink-soft" data-id="element-152">
            {t('booking.form.bookAnother')}
          </button>
        </motion.div>
      </div>;
  }
  return <div className="w-full bg-pink-lighter/20 pt-32 pb-20 min-h-screen" data-id="element-153">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-154">
        <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="text-center mb-12" data-id="element-155">
          <h1 className="text-4xl md:text-5xl font-bold text-navy mb-4" data-id="element-156">
            {t('booking.title')}
          </h1>
          <p className="text-xl text-gray-600" data-id="element-157">{t('booking.subtitle')}</p>
        </motion.div>

        <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="bg-white p-8 md:p-12 rounded-[2rem] shadow-xl border border-pink-50" data-id="element-158">
          <form onSubmit={handleSubmit} className="space-y-8" data-id="element-159">
            {/* Service Selection */}
            <div className="space-y-4" data-id="element-160">
              <h3 className="text-xl font-semibold text-navy border-b border-gray-100 pb-2" data-id="element-161">
                1. Service Details
              </h3>

              <div data-id="element-162">
                <label className="block text-sm font-medium text-navy mb-2" data-id="element-163">
                  {t('booking.form.serviceType')}
                </label>
                <select required value={formData.serviceType} onChange={e => setFormData({
                ...formData,
                serviceType: e.target.value
              })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-primary focus:ring-2 focus:ring-pink-primary/20 outline-none transition-all bg-white" data-id="element-164">
                  <option value="" disabled data-id="element-165">
                    {t('booking.form.selectService')}
                  </option>
                  <option value="house" data-id="element-166">{t('services.house.title')}</option>
                  <option value="apartment" data-id="element-167">
                    {t('services.apartment.title')}
                  </option>
                  <option value="office" data-id="element-168">{t('services.office.title')}</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-169">
                <div data-id="element-170">
                  <label className="block text-sm font-medium text-navy mb-2 flex items-center gap-2" data-id="element-171">
                    <Calendar className="w-4 h-4 text-pink-primary" data-id="element-172" />{' '}
                    {t('booking.form.date')}
                  </label>
                  <input type="date" required value={formData.date} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-primary focus:ring-2 focus:ring-pink-primary/20 outline-none transition-all" data-id="element-173" />
                </div>
                <div data-id="element-174">
                  <label className="block text-sm font-medium text-navy mb-2 flex items-center gap-2" data-id="element-175">
                    <Clock className="w-4 h-4 text-pink-primary" data-id="element-176" />{' '}
                    {t('booking.form.time')}
                  </label>
                  <input type="time" required value={formData.time} onChange={e => setFormData({
                  ...formData,
                  time: e.target.value
                })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-primary focus:ring-2 focus:ring-pink-primary/20 outline-none transition-all" data-id="element-177" />
                </div>
              </div>
            </div>

            {/* Personal Details */}
            <div className="space-y-4 pt-4" data-id="element-178">
              <h3 className="text-xl font-semibold text-navy border-b border-gray-100 pb-2" data-id="element-179">
                2. Your Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-180">
                <div data-id="element-181">
                  <label className="block text-sm font-medium text-navy mb-2" data-id="element-182">
                    {t('booking.form.name')}
                  </label>
                  <input type="text" required value={formData.name} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-primary focus:ring-2 focus:ring-pink-primary/20 outline-none transition-all" data-id="element-183" />
                </div>
                <div data-id="element-184">
                  <label className="block text-sm font-medium text-navy mb-2" data-id="element-185">
                    {t('booking.form.phone')}
                  </label>
                  <input type="tel" required value={formData.phone} onChange={e => setFormData({
                  ...formData,
                  phone: e.target.value
                })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-primary focus:ring-2 focus:ring-pink-primary/20 outline-none transition-all" data-id="element-186" />
                </div>
              </div>

              <div data-id="element-187">
                <label className="block text-sm font-medium text-navy mb-2 flex items-center gap-2" data-id="element-188">
                  <MapPin className="w-4 h-4 text-pink-primary" data-id="element-189" />{' '}
                  {t('booking.form.address')}
                </label>
                <input type="text" required value={formData.address} onChange={e => setFormData({
                ...formData,
                address: e.target.value
              })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-primary focus:ring-2 focus:ring-pink-primary/20 outline-none transition-all" data-id="element-190" />
              </div>

              <div data-id="element-191">
                <label className="block text-sm font-medium text-navy mb-2" data-id="element-192">
                  {t('booking.form.notes')}
                </label>
                <textarea rows={3} value={formData.notes} onChange={e => setFormData({
                ...formData,
                notes: e.target.value
              })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-primary focus:ring-2 focus:ring-pink-primary/20 outline-none transition-all resize-none" placeholder="Any specific areas to focus on, gate codes, etc." data-id="element-193"></textarea>
              </div>
            </div>

            <div className="pt-6" data-id="element-194">
              <button type="submit" className="w-full bg-pink-primary text-white py-4 rounded-xl font-semibold text-lg hover:bg-pink-hover transition-colors shadow-lg shadow-pink-soft flex items-center justify-center gap-2" data-id="element-195">
                <Sparkles className="w-5 h-5" data-id="element-196" />
                {t('booking.form.submit')}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>;
};